import { Injectable } from '@angular/core';
import { MechanicsService } from './mechanics.service';
import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class CaptchaService {

	private verificationStates: { [key: string]: boolean } = {};
	private forceDisabled: boolean = false; // Emergency override

	constructor(private ms: MechanicsService) { }

	/**
	 * Determines if CAPTCHA should be enabled based on environment
	 * In development/localhost, CAPTCHA is disabled for easier testing
	 */
	get isCaptchaEnabled(): boolean {
		// Emergency override for development
		if (this.forceDisabled) {
			return false;
		}
		
		// Multiple checks to ensure CAPTCHA is disabled in development
		const hostname = window.location.hostname;
		const port = window.location.port;
		
		// Check if running on localhost/development
		const isLocalDevelopment = 
			hostname === 'localhost' ||
			hostname === '127.0.0.1' ||
			hostname === '0.0.0.0' ||
			port === '4200' || // Angular dev server default port
			environment.gbd?.env === 'localhost' ||
			this.ms.isLocalHost;
		
		// For safety, default to disabled in development
		return !isLocalDevelopment;
	}

	/**
	 * Emergency method to force disable CAPTCHA (for development/debugging)
	 * Call this in browser console: window.captchaService.forceDisableCaptcha()
	 */
	forceDisableCaptcha(): void {
		this.forceDisabled = true;
		console.log('CAPTCHA has been force disabled');
	}

	/**
	 * Get the CAPTCHA URL for the altcha-widget
	 */
	get captchaURL(): string {
		return this.ms.environment.backendUrl + '/captcha?t=' + localStorage.getItem("gbd.hashSearches");
	}

	/**
	 * Check if a specific verification placeholder is verified
	 * @param placeholderName - The name of the verification placeholder (e.g., 'ev1')
	 */
	isVerified(placeholderName: string): boolean {
		// If CAPTCHA is disabled, always return true
		if (!this.isCaptchaEnabled) {
			return true;
		}
		
		return this.verificationStates[placeholderName] || false;
	}

	/**
	 * Handle verification event from altcha-widget
	 * @param event - The verification event
	 * @param placeholderName - The name of the verification placeholder
	 */
	async onVerify(event: any, placeholderName: string): Promise<void> {
		if (event.type === 'verified') {
			this.verificationStates[placeholderName] = true;
		}
	}

	/**
	 * Reset verification state for a specific placeholder
	 * @param placeholderName - The name of the verification placeholder
	 */
	resetVerification(placeholderName: string): void {
		this.verificationStates[placeholderName] = false;
	}

	/**
	 * Reset all verification states
	 */
	resetAllVerifications(): void {
		this.verificationStates = {};
	}
}
